import { createAsyncThunk } from '@reduxjs/toolkit';
import ApiService from '../../../infrastructure/services/api/services';
import { apiConfig } from '../../../application/configurations/apiConfig';
import { ApiConfig } from '../../../application/configurations/IConfig';
import { toggleLoading } from '../../components/pageLoader/loaderReducer';
import { appConstants } from '../../../application/configurations/constants';
import { getPreviewEmailFromUrl, UtilityService } from '../../../infrastructure/services/utils';
import { resultsConfigurations } from './config';
import {
  enableFlagForSuggestedOrRecentSearch,
  setBestBetFlag,
  setInsightsFlag,
  clearInsightsLeads
} from './searchReducer';
import { store } from '../../../application/store';
import AnalyticsEvents from '../../../infrastructure/services/analytics/analyticsUseCase';
import {
  buildRefinementArrays,
  pushSearchQueryEvent,
  pushApplyRefinerEvent,
  pushPaginationEvent,
  pushTabClickEvent,
  pushPageSizeEvent
} from '../../../infrastructure/services/analyticsNew';
// COMMENTED OUT: Adobe Analytics - will be replaced with new Adobe Launch script
// import { AdobeAnalyticsEvents } from '../../../infrastructure/services/analytics/adobeAnalyticsUseCase';
import { setHomePageSearchFlag } from './searchReducer';
import { PeopleSortConfig } from '../../components/sort/config';
import { resultsConstants } from './constants';
import { cardConfig } from '../../components/cards/config';
import envConfig, { isLocalOrDevEnv } from '../../../application/configurations/envConfig';
import JsonData from '../../../application/configurations/refiner.json';
import HybridSearchJsonData from '../../../application/configurations/HybridSearchRefinersSequence.json';
import GeographyData from '../../../application/configurations/geographyTaxonomy.json';

/**
 * class for processing async functions based on actions
 */
class Search {
  private searchApiConfig: ApiConfig;

  private hybridSearchApiConfig: ApiConfig;

  private isSemanticSearchEnabled: boolean = false;

  constructor() {
    this.searchApiConfig = { ...apiConfig.search };
    this.hybridSearchApiConfig = { ...apiConfig.HybridSearch };
  }

  //Call search and hybrid search API based on toggle
  private async getSearchConfig(searchTerm: string, vertical: string) {

    // If toggle feature is enabled, set isSemanticSearchEnabled based on user's searchMode preference from API
    if (envConfig.isToggleAISearchEnabled) {
      this.isSemanticSearchEnabled = store.getState().search.searchMode === 'Semantic';
    } else {
      this.isSemanticSearchEnabled = false;
    }

    return this.hybridSearchApiConfig;
  }

  /**
   * async method with redux store middleware Thunk wrapper
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * this is also pass the response to
   * @param   {object} request the request object containing search query term & an optional start row.
   * @return  {object} return api response
   */
  doSearch = createAsyncThunk(
    'search',
    async (
      request: {
        searchTerm: string;
        vertical: string;
        filterByDate: number;
        accounts: Array<Object>;
        refinersClicked: any;
        flagForContentAge: boolean;
        flagForClear: boolean;
        startRow: number;
        homepageSearch: boolean;
        sortByValue: {
          name: string;
          Value: string;
          Direction: number;
          SortType: number;
        };
        refinerValues: any;
        clearAll: boolean;
        dropdownAction?: boolean;
        paginationAction?: 'Previous Page' | 'Next Page' | null;
        pageSizeAction?: number | null;
      },
      thunkApi
    ) => {
      // Check if we have the necessary authentication
      if (!request.accounts || request.accounts.length === 0) {
        thunkApi.dispatch(toggleLoading(false));

        return {
          results: [],
          resultCount: 0,
          profilePhotoCollection: [],
          refiners: [],
          latency: 0,
          nestedRefiners: []
        };
      }

      // dispatching action to show loader
      thunkApi.dispatch(toggleLoading(true));
      let responseData: any = [];
      let resultCount: number = 0;
      let peopleAccountId: Array<string | undefined> = [];
      let profilePhotoCollection: any = [];
      let refinedVerticals: any = [];
      let latency: number = 0;
      let nestedRefinersFinal: any = [];
      let impersonationInfo: any = null;
      const filterObject: any = JsonData.filter(
        (item) => item.vertical === request.vertical
      );
      const HybridSearchSequence: any = HybridSearchJsonData.filter(
        (item) => item.vertical === request.vertical
      )[0];
      let resultsLimit: any = resultsConfigurations.resultsPerPage.filter(
        (option) => option.vertical === request.vertical
      );
      let noOfItemsPerPage = JSON.parse(
        localStorage.getItem(appConstants.noOfItemsPerPage) || '{}'
      );
      let itemsToSetPerPage: number = noOfItemsPerPage
        ? noOfItemsPerPage
        : appConstants.defaultItemsPerPage;
      const modelObj = UtilityService.getRequestModel(request.vertical);
      const returnFields = UtilityService.getReturnFields(request.vertical);
      const searchConfig = {
        ...await this.getSearchConfig(request.searchTerm, request.vertical)
      };
      searchConfig.accountInfo = request.accounts;
      searchConfig.data = {
        ...searchConfig.data,
        Query: request.searchTerm,
        verticals: request.vertical,
        Refiners:
          filterObject && filterObject.length > 0
            ? Object.keys(filterObject[0])
            : [appConstants.RefinerName],
        Size:
          request.startRow > resultsLimit[0].count - itemsToSetPerPage
            ? resultsLimit[0].count - request.startRow
            : itemsToSetPerPage,
        From: request.startRow ? request.startRow : 0,
        ReturnFields: returnFields,
        Sort: [],
        SearchType: this.isSemanticSearchEnabled
          ? appConstants.searchType.Semantic
          : appConstants.searchType.Keyword
      };

      // If URL contains a valid preview=<emailaddress>, forward it as
      // PreviewEmail to HybridSearch. The helper handles HashRouter URLs
      // (query lives inside window.location.hash) and validates the email
      // format so malformed input is never forwarded. Authorization for who
      // may impersonate whom is enforced by the backend, not the client.
      const previewEmail = getPreviewEmailFromUrl();
      if (previewEmail) {
        searchConfig.data.PreviewEmail = previewEmail;
      }

      // Initialize Filters
      searchConfig.data.Filters = {
        Condition: 'AND',
        Rules: []
      };

      // filter to show only published content in case of documents vertical.
      searchConfig.data.Filters.Rules.push({
        Field: 'ESC_EYCONTENTSTATUS',
        Type: 'String',
        Operator: ':',
        Value: 'Publish'
      });

      // filter search by Date of content age API call condition
      if (request.filterByDate && modelObj.contentAge) {
        searchConfig.data.Filters.Rules.push({
          Field: modelObj.contentAge,
          Type: 'Date',
          Operator: 'Range',
          Value: `${UtilityService.getMonthDates(
            request.filterByDate
          )};${new Date().toISOString()}`
        });
      }
      if (
        request.vertical === appConstants.verticals.All ||
        request.vertical === appConstants.verticals.News
      ) {
        searchConfig.data.PersonalFilters = {
          Condition: 'AND',
          Rules: []
        };

        let geographyFilters: string[] = [];
        let userInfo = null;
        try {
          userInfo = sessionStorage.UserInfo
            ? JSON.parse(sessionStorage.UserInfo)
            : null;
        } catch (parseError) {
          userInfo = null;
        }

        if (userInfo && userInfo.EYCountry) {

          const path = UtilityService.findGeographyPath(
            GeographyData.children,
            userInfo.EYCountry,
            []
          );

          if (path) {
            geographyFilters.push(...[...path].reverse());
          } else {
            geographyFilters.push(userInfo.EYCountry);
          }
        }

        geographyFilters.push('Global');

        // Check if there are multiple geography filters
        if (geographyFilters.length > 1) {
          searchConfig.data.PersonalFilters.Rules.push({
            Field: 'ESC_GEOGRAPHICAPPLICABILITY',
            Type: 'String',
            Operator: ':',
            Values: geographyFilters
          });
          searchConfig.data.PersonalFilters.Rules.push({
            Field: 'ESC_GEOGRAPHICAPPLICABILITY_ALT_LABEL',
            Type: 'String',
            Operator: ':',
            Values: geographyFilters
          });
        } else {
          searchConfig.data.PersonalFilters.Rules.push({
            Field: 'ESC_GEOGRAPHICAPPLICABILITY',
            Type: 'String',
            Operator: ':',
            Value: geographyFilters[0]
          });
          searchConfig.data.PersonalFilters.Rules.push({
            Field: 'ESC_GEOGRAPHICAPPLICABILITY_ALT_LABEL',
            Type: 'String',
            Operator: ':',
            Value: geographyFilters[0]
          });
        }
      }

      // Refiners API call condition
      if (
        request.refinerValues !== undefined &&
        request.refinerValues.length > 0
      ) {
        if (request.vertical === appConstants.verticals.People) {
          searchConfig.data.Filters = {
            Condition: 'AND',
            Rules: []
          };
        }
        request.refinerValues.forEach((item: any) => {
          if (
            item[Object.keys(item)[0]] !== undefined &&
            item[Object.keys(item)[0]].length > 0
          ) {
            // Condition to check if selected refiner is more than one value
            UtilityService.selectedRefiners(
              item,
              searchConfig.data.Filters.Rules
            );
          }
        });
      }

      //  sort by filter API call condition
      if (request.sortByValue && request.sortByValue !== undefined) {
        searchConfig.data.Sort.push({
          FieldName: request.sortByValue.Value,
          Ascending: request.sortByValue.Direction == 1 ? false : true
        });
        if (
          request.vertical === appConstants.verticals.People &&
          (request.sortByValue.Value ===
            PeopleSortConfig.filterOptions[1].Value ||
            request.sortByValue.Value ===
            PeopleSortConfig.filterOptions[3].Value)
        ) {
          searchConfig.data.Sort.push({
            FieldName:
              request.sortByValue.Value ===
                PeopleSortConfig.filterOptions[1].Value
                ? PeopleSortConfig.filterOptions[3].Value
                : PeopleSortConfig.filterOptions[1].Value,
            Ascending: request.sortByValue.Direction == 1 ? false : true
          });
        }
      }

      if (request.vertical === appConstants.verticals.All) {
        searchConfig.data.Filters.Rules.push({
          Field: appConstants.ElasticFields.vertical,
          Type: 'String',
          Operator: ':',
          Values: [
            appConstants.verticals.Credentials,
            appConstants.verticals.Data,
            appConstants.verticals.Documents,
            appConstants.verticals.News,
            appConstants.verticals.People,
            appConstants.verticals.Videos,
            appConstants.nonVerticals.Intranet
          ]
        });
      } else {
        let verticalText =
          request.vertical.toLowerCase() === appConstants.adobeAnalytics.browseConfigs.solutions.toLocaleLowerCase()
            ? appConstants.verticals.Documents
            : request.vertical;
        verticalText =
          request.vertical.toLowerCase() === appConstants.nonVerticals.PolicyFinder.toLocaleLowerCase()
            ? appConstants.verticals.Documents
            : verticalText;
        verticalText =
          request.vertical.toLowerCase() === appConstants.adobeAnalytics.browseConfigs.proposals.toLocaleLowerCase()
            ? appConstants.verticals.Documents
            : verticalText;
        verticalText =
          request.vertical.toLowerCase() === appConstants.adobeAnalytics.browseConfigs.credentials.toLocaleLowerCase()
            ? appConstants.verticals.Credentials
            : verticalText;
        verticalText =
          request.vertical.toLowerCase() === appConstants.EYPKC.toLocaleLowerCase()
            ? appConstants.verticals.Documents
            : verticalText;
        searchConfig.data.Filters.Rules.push({
          Field: appConstants.ElasticFields.vertical,
          Type: 'String',
          Operator: ':',
          Value: verticalText
        });
      }
      await ApiService.requestApi([searchConfig])
        .then(
          (response: { [key: string]: any }) => {
            // Variable for backend failure counts i.e., isError property count in the actual API response object
            let errorCount: number = 0;
            // Extract impersonation flags so the UI can show the correct error message.
            if (response && response[0] && response[0].result) {
              const result = response[0].result;
              impersonationInfo = {
                userName: result.userName ?? null,
                previewMailValid: result.previewMailValid ?? null,
                isDataImpersonated: result.isDataImpersonated ?? null
              };
            }
            if (response && response[0] && response[0].result.relevantResults) {
              try {
                // Refiners
                // First Level
                let level1Transformation = UtilityService.convertRefinersLevel1(
                  JSON.parse(response[0].result.refinementResultsJSON)
                );

                // Second Level
                level1Transformation.map((refine: any) => {
                  let object1: any = {};
                  //Condition to check if the Refiner is from AEM using path keyword.
                  object1 =
                    refine.title.indexOf('UFPATH') > -1
                      ? {
                        title: refine.title,
                        refId: refine.refId,
                        ID: Object.keys(HybridSearchSequence).find(
                          (k) => HybridSearchSequence[k] === refine.title
                        ),
                        source: UtilityService.convertRefinersLevel2({
                          RefinementResults: refine.source.slice(1)
                        })
                      }
                      : {
                        title: refine.title,
                        refId: refine.refId,
                        ID: Object.keys(HybridSearchSequence).find(
                          (k) => HybridSearchSequence[k] === refine.title
                        ),
                        source: UtilityService.convertRefinersLevel2({
                          RefinementResults: refine.source
                        })
                      };

                  object1.title =
                    filterObject[0] &&
                      filterObject[0][object1.title] !== undefined &&
                      object1.title !== 'vertical'
                      ? filterObject[0][object1.title]
                      : object1.title;

                  nestedRefinersFinal.push(object1);
                });

                // Dont remove this console until we implement Refiners.
                // Sort refiners based on the order defined in HybridSearchRefinersSequence.json
                nestedRefinersFinal = [...nestedRefinersFinal].sort(
                  (a, b) => {
                    if (a.ID === undefined) return 1; // Place undefined at the end

                    if (b.ID === undefined) return -1; // Place undefined at the end

                    return a.ID - b.ID; // Sort numerically
                  }
                );
              } catch (error) {
                if (isLocalOrDevEnv) {
                  console.log(error);
                }
              }
              // Refiners logics ends here.

              response[0].result.relevantResults.forEach((record: any) => {
                peopleAccountId.push(record.accountId);
                responseData.push(
                  UtilityService.getResponseModel(record.vertical, record)
                );
              });
              // Setting up the Results Found count by decreasing with the backend failure count(errorCount).
              // This will fix for the backend failure scenarios i.e., when the backend returns any error in the response.
              resultCount =
                responseData.length > 0
                  ? response[0].result.totalRelevantResults - errorCount
                  : responseData.length;
              refinedVerticals = response[0].result.refinementResults;
              latency = response[0].result.totalTime;
              const validPeopleAccountIds = peopleAccountId.filter((id) => id);
              if (validPeopleAccountIds.length) {
                thunkApi.dispatch(
                  this.getUserProfilePic({
                    peopleAccountIds: validPeopleAccountIds,
                    accounts: request.accounts
                  })
                );
              }
            }
          },
          (error) => {
            throw new Error(error);
          }
        )
        .finally(() => {
          // Insert query recommendation for search history tracking
          if (resultCount && resultCount > 0) {
            this.insertQueryRecommendation(
              request.searchTerm,
              request.vertical,
              store.getState().general.userInfo?.userEmail || '',
              request.accounts
            );
          }

          if (request.paginationAction) {
            const itemsPerPage = Number(
              localStorage.getItem(appConstants.noOfItemsPerPage) ||
                appConstants.defaultItemsPerPage
            );
            const pageLimit =
              resultsConfigurations.resultsPerPage.find(
                (option) => option.vertical.toLowerCase() === request.vertical.toLowerCase()
              )?.count ?? resultCount;
            const totalPages = Math.max(
              1,
              Math.ceil(Math.min(resultCount, pageLimit) / itemsPerPage)
            );
            const currentPage = Math.floor(request.startRow / itemsPerPage) + 1;
            const { refinementType, refinementValue } = buildRefinementArrays(
              request.refinerValues ?? [],
              request.vertical
            );
            pushPaginationEvent(
              request.paginationAction,
              currentPage,
              totalPages,
              request.searchTerm,
              resultCount,
              request.vertical,
              refinementType,
              refinementValue
            );
          } else if (request.pageSizeAction) {
            const { refinementType, refinementValue } = buildRefinementArrays(
              request.refinerValues ?? [],
              request.vertical
            );
            pushPageSizeEvent(
              request.pageSizeAction,
              request.searchTerm,
              resultCount,
              request.vertical,
              refinementType,
              refinementValue
            );
          } else {
            // Calling the Analytics Events
            this.callAnalytics(request, resultCount);

            // Adobe Analytics: push search query click-action event
            const searchFlag = store.getState().search.flagForSuggestedOrRecentSearch;
          // Refiner-triggered searches push their own refiner event instead of a Search event.
          const isRefinerTriggered =
            !!request.clearAll ||
            !!(request.refinersClicked && (request.flagForContentAge || request.flagForClear)) ||
            !!request.dropdownAction;
          if (!request.paginationAction && isRefinerTriggered) {
            // no Search event
          } else if (searchFlag.startsWith('Related Search Click:')) {
            const relatedTerm = searchFlag.slice('Related Search Click:'.length);
            pushSearchQueryEvent(
              `Related searches:${relatedTerm}`,
              relatedTerm,
              resultCount,
              { controlType: 'button', searchType: request.vertical }
            );
          } else if (searchFlag.startsWith('Vertical Tab Click:')) {
            const tabLabel = searchFlag.slice('Vertical Tab Click:'.length);
            pushTabClickEvent(
              tabLabel,
              request.searchTerm,
              resultCount,
              window.location.href
            );
          } else if (!request.paginationAction) {
            const searchQueryName =
              searchFlag === 'Recent Search Click'
                ? 'Recent Search'
                : searchFlag === 'Suggestions Click'
                ? 'Suggested Search'
                : 'Search';
            pushSearchQueryEvent(searchQueryName, request.searchTerm, resultCount);
          }
          }

          // Adobe Analytics: push apply / clear refiner click-action event
          if (
            !request.paginationAction &&
            !request.pageSizeAction &&
            request.refinersClicked &&
            (request.flagForContentAge || request.flagForClear) &&
            !request.clearAll
          ) {
            // refinersClicked is set as a plain object {key:[]} or an array [{key:[]}] depending
            // on whether the store already had refiners — normalise to array format.
            const clickedArr: Record<string, string[]>[] = Array.isArray(request.refinersClicked)
              ? request.refinersClicked
              : [request.refinersClicked];
            const refinementType: string[] = [];
            const refinementValue: string[] = [];
            const verticalRefinerMap: Record<string, string | undefined> =
              JsonData.find(
                (item: any) =>
                  item.vertical.toLowerCase() === request.vertical.toLowerCase()
              ) ?? {};
            clickedArr.forEach((refinerObj: Record<string, string[]>) => {
              const key = Object.keys(refinerObj)[0];
              const values: string[] = Object.values(refinerObj)[0];
              if (key && values && values.length > 0) {
                const displayName: string = verticalRefinerMap[key] ?? key;
                refinementType.push(displayName);
                values.forEach((val: string) => {
                  const leafValue = val.includes('\\') ? val.split('\\').pop()! : val;
                  refinementValue.push(leafValue);
                });
              }
            });
            if (refinementType.length > 0) {
              if (request.flagForClear) {
                // Clear button clicked inside a filter dropdown (checked first — takes priority
                // over stale flagForContentAge which may still be true from a previous Apply)
                pushApplyRefinerEvent(
                  request.searchTerm,
                  resultCount,
                  request.vertical,
                  refinementType,
                  refinementValue,
                  { name: 'Clear Refiner', controlType: 'button', includeNullInstances: false }
                );
              } else if (request.flagForContentAge) {
                // Apply button clicked
                pushApplyRefinerEvent(
                  request.searchTerm,
                  resultCount,
                  request.vertical,
                  refinementType,
                  refinementValue
                );
              }
            }
          }

          // injecting page load adobe Analytics Events
          // COMMENTED OUT: Adobe Analytics - will be replaced with new Adobe Launch script
          // AdobeAnalyticsEvents.injectAdobePageLoadEvents(
          //   request.searchTerm,
          //   resultCount,
          //   store.getState().general.userInfo,
          //   request.vertical,
          //   false
          // );

          thunkApi.dispatch(setHomePageSearchFlag(false));
          thunkApi.dispatch(enableFlagForSuggestedOrRecentSearch(''));
        });

      return {
        results: responseData,
        resultCount: resultCount,
        profilePhotoCollection: profilePhotoCollection,
        refiners: refinedVerticals,
        latency: latency,
        nestedRefiners: nestedRefinersFinal,
        isSemanticSearchEnabled: this.isSemanticSearchEnabled,
        impersonationInfo: impersonationInfo
      };
    }
  );

  /**
   * Method to insert query recommendations for search analytics
   * Called alongside Hybrid API on every search (Enter, Tab, etc.)
   * @param   {string} searchTerm  search query term
   * @param   {string} vertical  search vertical
   * @param   {string} userEmail  hashed user email from store
   * @param   {Array<Object>} accounts  msal accounts
   */
  insertQueryRecommendation = async (
    searchTerm: string,
    vertical: string,
    userEmail: string,
    accounts: Array<Object>
  ): Promise<void> => {
    try {
      const queryRecommendationConfig = { ...apiConfig.queryRecommendationsInsert };
      queryRecommendationConfig.accountInfo = accounts;
      queryRecommendationConfig.data = {
        Time: new Date().toISOString(),
        Email: userEmail,
        Query: searchTerm,
        Vertical: vertical
      };
      await ApiService.requestApi([queryRecommendationConfig]);
    } catch (error) {
      if (isLocalOrDevEnv) {
        console.warn('Failed to insert query recommendation:', error);
      }
    }
  };

  /**
   * Method to trigger analytics actions
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * @param  {object} request the request object containing search query term & an optional start row.
   * @param  {number} resultCount search result count
   */
  callAnalytics = (
    request: {
      searchTerm: string;
      vertical: string;
      filterByDate?: number;
      accounts: Array<Object>;
      startRow?: number | undefined;
      homepageSearch: boolean;
      refinersClicked?: Array<Object>;
      flagForContentAge?: boolean;
      flagForClear?: boolean;
      clearAll?: boolean;
    },
    resultCount: number | undefined
  ) => {
    const flagForSuggestedOrRecentSearch =
      store.getState().search.flagForSuggestedOrRecentSearch;

    // Tab clicks are plain searches for the legacy analytics, not suggestion/recent clicks.
    const isVerticalTabClick = flagForSuggestedOrRecentSearch.startsWith(
      'Vertical Tab Click:'
    );

    if (flagForSuggestedOrRecentSearch !== '' && !isVerticalTabClick) {
      const wasRecentSearch =
        flagForSuggestedOrRecentSearch ===
          resultsConfigurations.RecentSearchClick
          ? 'true'
          : 'false';
      // Calling the RecentSearch and Suggestion Click analytics usecase
      AnalyticsEvents.recentSearchAndSuggestionsClick(
        request.searchTerm,
        request.accounts,
        request.homepageSearch,
        wasRecentSearch,
        resultCount
      );
    } else {
      // Calling the Search Action analytics usecase
      AnalyticsEvents.searchAction(
        request.searchTerm,
        request.accounts,
        request.homepageSearch,
        request.vertical,
        resultCount
      );
    }
    if (
      request.vertical !== appConstants.verticals.People &&
      !request.clearAll
    ) {
      AnalyticsEvents.refinerAction(
        request.searchTerm,
        request.accounts,
        request.homepageSearch,
        request.filterByDate,
        request.refinersClicked,
        request.flagForContentAge,
        request.flagForClear,
        resultCount,
        request.vertical
      );
    } else if (
      request.vertical === appConstants.verticals.People &&
      request.refinersClicked &&
      request.refinersClicked.length > 0 &&
      !request.clearAll
    ) {
      AnalyticsEvents.refinerAction(
        request.searchTerm,
        request.accounts,
        request.homepageSearch,
        request.filterByDate,
        request.refinersClicked,
        request.flagForContentAge,
        request.flagForClear,
        resultCount,
        request.vertical
      );
    }
  };

  /**
   * Method to get best bets results
   * @param   {object} requestParams the request object containing search query term & an optional start row.
   * @return  {object} return api response
   */
  bestbets = createAsyncThunk(
    'bestBets',
    async (
      requestParams: {
        searchTerm: string;
        vertical: string;
        accounts: Array<Object>;
      },
      thunkApi
    ): Promise<any> => {
      let responseData: any = [];
      const returnFields = UtilityService.getReturnFields(requestParams.vertical);
      const searchConfig = await this.getSearchConfig(requestParams.searchTerm, requestParams.vertical);
      searchConfig.accountInfo = requestParams.accounts;
      searchConfig.data = {
        ...searchConfig.data,
        Query: requestParams.searchTerm,
        verticals: requestParams.vertical,
        Size: 3,
        Filters: {
          Condition: 'AND',
          Rules: []
        },
        ReturnFields: returnFields,
        SearchType: appConstants.searchType.Keyword
      };
      let geographyFiltersBestBets: string[] = [];
      let userInfo = null;
      try {
        userInfo = sessionStorage.UserInfo
          ? JSON.parse(sessionStorage.UserInfo)
          : null;
      } catch (parseError) {
        userInfo = null;
      }

      if (userInfo && userInfo.EYCountry) {

        const path = UtilityService.findGeographyPath(
          GeographyData.children,
          userInfo.EYCountry,
          []
        );

        if (path) {
          geographyFiltersBestBets.push(...[...path].reverse());
        } else {
          geographyFiltersBestBets.push(userInfo.EYCountry);
        }
        if (geographyFiltersBestBets.indexOf(userInfo.EYCountry) === -1) {
          geographyFiltersBestBets.push(userInfo.EYCountry);
        }
      }
      geographyFiltersBestBets.push('Global');
      if (geographyFiltersBestBets.length > 1) {
        searchConfig.data.Filters.Rules.push({
          Field: 'ESC_GEOGRAPHICAPPLICABILITY',
          Type: 'String',
          Operator: ':',
          Values: geographyFiltersBestBets
        });
        searchConfig.data.Filters.Rules.push({
          Field: 'ESC_GEOGRAPHICAPPLICABILITY_ALT_LABEL',
          Type: 'String',
          Operator: ':',
          Values: geographyFiltersBestBets
        });
      } else {
        searchConfig.data.Filters.Rules.push({
          Field: 'ESC_GEOGRAPHICAPPLICABILITY',
          Type: 'String',
          Operator: ':',
          Value: geographyFiltersBestBets[0]
        });
        searchConfig.data.Filters.Rules.push({
          Field: 'ESC_GEOGRAPHICAPPLICABILITY_ALT_LABEL',
          Type: 'String',
          Operator: ':',
          Value: geographyFiltersBestBets[0]
        });
      }
      await ApiService.requestApi([searchConfig])
        .then(
          (response: { [key: string]: any }) => {
            if (response && response[0] && response[0].result.relevantResults) {
              response[0].result.relevantResults.forEach((record: any) => {
                let federatorData = JSON.parse(record.FederatorMetadata);
                if (federatorData && federatorData.length > 0) {
                  let backendName = federatorData.find((obj: any) => {
                    return obj.Key === 'FederatorBackend';
                  }).Value;
                  if (
                    backendName === appConstants.backendDataSources.bestbets ||
                    backendName === appConstants.backendDataSources.bestbets_dev ||
                    backendName === appConstants.backendDataSources.bestbets_qa
                  ) {
                    record.vertical = appConstants.BestBets;
                  }
                  responseData.push(
                    UtilityService.getResponseModel(record.vertical, record)
                  );
                }
              });
            }
          },
          (error) => {
            throw new Error(error);
          }
        )
        .finally(() => {
          if (responseData && responseData.length > 0) {
            thunkApi.dispatch(setBestBetFlag(true));
          } else {
            thunkApi.dispatch(setBestBetFlag(false));
          }
        });

      return {
        bestBetsResults: responseData.sort((a: any, b: any) =>
          a.Title.localeCompare(b.Title)
        )
      };
    }
  );

  /**
   * Method to get Insight results
   * @param   {object} requestParams the request object containing search query term & an optional start row.
   * @return  {object} return api response
   */
  insights = createAsyncThunk(
    'insights',
    async (
      requestParams: {
        searchTerm: string;
        vertical: string;
        accounts: Array<Object>;
      },
      thunkApi
    ): Promise<any> => {
      let responseData: any = [];
      const returnFields = UtilityService.getReturnFields(requestParams.vertical);
      //  const searchConfig = { ...this.searchApiConfig };
      const searchConfig = {
        ...await this.getSearchConfig(
          requestParams.searchTerm,
          appConstants.InsightsData
        )
      };
      searchConfig.accountInfo = requestParams.accounts;
      searchConfig.data = {
        ...searchConfig.data,
        Query: requestParams.searchTerm,
        verticals: 'InsightsPane',
        Filters: {
          Condition: 'OR',
          Rules: [
            {
              Field: 'contentType',
              Type: 'String',
              Operator: ':',
              Values: [
                'EY Knowledge Buyer Page',
                'EY Knowledge FoP Page',
                'EY Knowledge Solution Page',
                'EY Topic Page'
              ]
            }
          ]
        },
        Size: 1,
        ReturnFields: returnFields
      };

      await ApiService.requestApi([searchConfig])
        .then(
          (response: { [key: string]: any }) => {
            if (response && response[0] && response[0].result.relevantResults) {
              response[0].result.relevantResults.forEach((record: any) => {
                responseData.push(
                  UtilityService.getResponseModel(record.vertical, record)
                );
              });
            }

            let peopleEmailIds: Array<string | undefined> = [];
            let contentType: string =
              response[0] && responseData[0].contentType;

            switch (contentType) {
              case cardConfig.contentTypes.EYKnowledgeFoPPage:
              case cardConfig.contentTypes.EYKnowledgeSolutionPage:
                peopleEmailIds =
                  responseData[0].solutionLeadersEmail.split(';');
                break;

              case cardConfig.contentTypes.EYKnowledgeBuyerPage:
              case cardConfig.contentTypes.EYTopicPage:
                peopleEmailIds = responseData[0].eyContactEmail.split(';');
                break;

              default:
                break;
            }

            // Graph API call for Insights leads data fetch
            const validPeopleEmails = peopleEmailIds.filter((id) => id);
            if (validPeopleEmails.length) {
              thunkApi.dispatch(
                this.getInsightLeadsFromGraph({
                  peopleEmails: validPeopleEmails,
                  accounts: requestParams.accounts
                })
              );

              thunkApi.dispatch(
                this.getInsightLeadsPhotosFromGraph({
                  peopleEmails: validPeopleEmails,
                  accounts: requestParams.accounts
                })
              );
            } else {
              thunkApi.dispatch(clearInsightsLeads());
            }
          },
          (error) => {
            throw new Error(error);
          }
        )
        .finally(() => {
          if (responseData && responseData.length > 0) {
            thunkApi.dispatch(setInsightsFlag(true));
          } else {
            thunkApi.dispatch(setInsightsFlag(false));
          }
        });

      return {
        insightResults: responseData
      };
    }
  );

  /**
   * Method to get Insight Leads data from graph API
   * @param   {object} requestParams the request object containing leadsemails.
   * @return  {object} return api response
   */
  public getInsightLeadsFromGraph = createAsyncThunk(
    'fetchLeadsData',
    async (requestParams: {
      peopleEmails: Array<string | undefined>;
      accounts: Array<Object>;
    }): Promise<any> => {
      let graphApiConfigArr: any = [];
      const batchSize = 20;
      for (
        let batchIndex = 0;
        batchIndex < requestParams.peopleEmails.length;
        batchIndex += batchSize
      ) {
        const chunkPeopleAccountEmails = requestParams.peopleEmails.slice(
          batchIndex,
          batchIndex + batchSize
        );
        let insightsLeadBatchCallObject =
          this.getLeadProfileBatchCol(chunkPeopleAccountEmails);
        graphApiConfigArr.push({
          ...apiConfig.getProfilePicture,
          data: insightsLeadBatchCallObject,
          accountInfo: requestParams.accounts
        });
      }

      return ApiService.requestApi(graphApiConfigArr);
    }
  );

  /**
   * Method to get Insight Leads photos from graph API
   * @param   {object} requestParams the request object containing leadsemails.
   * @return  {object} return api response
   */
  public getInsightLeadsPhotosFromGraph = createAsyncThunk(
    'fetchLeadsPhotos',
    async (requestParams: {
      peopleEmails: Array<string | undefined>;
      accounts: Array<Object>;
    }): Promise<any> => {
      let graphApiConfigArr: any = [];
      const batchSize = 20;
      for (
        let batchIndex = 0;
        batchIndex < requestParams.peopleEmails.length;
        batchIndex += batchSize
      ) {
        const chunkPeopleAccountEmails = requestParams.peopleEmails.slice(
          batchIndex,
          batchIndex + batchSize
        );
        let insightsLeadBatchCallObject =
          this.getLeadsProfilePhotoBatchCol(chunkPeopleAccountEmails);
        graphApiConfigArr.push({
          ...apiConfig.getProfilePicture,
          data: insightsLeadBatchCallObject,
          accountInfo: requestParams.accounts
        });
      }

      return ApiService.requestApi(graphApiConfigArr);
    }
  );

  getLeadProfileBatchCol = (leadEmails: any) => {
    const postDataPhoto = leadEmails.map((leadEmail: string) => ({
      id: leadEmail,
      method: 'GET',
      url: `/users/${leadEmail}?$select=displayName,mail,jobTitle,id`
    }));

    return { requests: postDataPhoto };
  };

  getLeadsProfilePhotoBatchCol = (leadEmails: any) => {
    const postDataPhoto = leadEmails.map((leadEmail: string) => ({
      id: leadEmail,
      method: 'GET',
      url: `/users/${leadEmail}/photo/$value`
    }));

    return { requests: postDataPhoto };
  };

  public getUserProfilePic = createAsyncThunk(
    'userProfilePhotos',
    async (requestParams: {
      peopleAccountIds: Array<string | undefined>;
      accounts: Array<Object>;
    }): Promise<any> => {
      let graphApiConfigArr: any = [];
      const batchSize = 20;
      for (
        let batchIndex = 0;
        batchIndex < requestParams.peopleAccountIds.length;
        batchIndex += batchSize
      ) {
        const chunkPeopleAccountIds = requestParams.peopleAccountIds.slice(
          batchIndex,
          batchIndex + batchSize
        );
        let peopleProfileBatchCallObject = this.getPeopleProfileBatchCol(
          chunkPeopleAccountIds
        );
        graphApiConfigArr.push({
          ...apiConfig.getProfilePicture,
          data: peopleProfileBatchCallObject,
          accountInfo: requestParams.accounts
        });
      }

      return ApiService.requestApi(graphApiConfigArr);
    }
  );

  getPeopleProfileBatchCol = (peopleAccountIds: any) => {
    const postDataPhoto = peopleAccountIds.map((peopleAccountId: string) => ({
      id: peopleAccountId,
      method: 'GET',
      url: `/users/${peopleAccountId}/photo/$value`
    }));

    return { requests: postDataPhoto };
  };

  /**
   * async method to trigger suggestions search action
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * this is also pass the response to
   * @param   {string} searchTerm  search query term
   */
  queryRecommondations = createAsyncThunk(
    'queryRecommondations',
    async (request: { searchTerms: string; visibleSuggestions: number }) => {
      const suggestionsConfig = { ...apiConfig.querySuggestions };
      suggestionsConfig.data.Query = request.searchTerms;
      let responseData: any = [];
      await ApiService.requestApi([suggestionsConfig])
        .then((suggestionsArray: any) => {
          if (suggestionsArray.length > 0) {
            suggestionsArray[0].map((item: any) => {
              responseData.push(item);
            });
          }
          if (responseData.length > 0) {
            responseData.push(`Search for ${request.searchTerms}`);
          }
        })
        .catch((error) => {
          if (isLocalOrDevEnv) {
            console.log('Error' + error);
          }
        });

      return {
        suggestionList: responseData
      };
    }
  );
  /** 
   * Method to fetch the Ontology Response 
   * We are using the Ontology API to fetch the Ontology Response and parsing through it to obtain all
   the object labels and storing it in an array.
   *  @param   {object} request the request object containing search query term */

  fetchOntologyData = createAsyncThunk(
    'fetchOntologyData',
    async (request: { searchTerm: string }) => {
      const ontologyConfig = apiConfig.getOntologyData;
      ontologyConfig.baseUrl = envConfig.ontology.BaseUrl;
      ontologyConfig.data.searchText = request.searchTerm;
      const ontologyResponse: any = await ApiService.requestApi([
        ontologyConfig
      ]);

      let objectLabelsArray: any[] = [];

      ontologyResponse.map((result: any) => {
        result.result.concepts.map((concept: any) => {
          if (concept.label && concept.label.value) {
            objectLabelsArray.push(concept.label.value);
          }
        });
      });

      return {
        ontologyList: objectLabelsArray
      };
    }
  );

  /**
   * Method to trigger API endpoint to get Preview Stats
   * this is used to access the preview stats based on api endpoint configuration and
   * add dynamic params in the same fetch.
   * @param  {string} aemId AEM Id to get the Preview Stats for specific item
   */
  public getPreviewStats = createAsyncThunk(
    'getPreviewStats',
    async (request: { aemId: string }) => {
      const userDetails = store.getState().general;
      let responseData: any = [];

      await fetch(
        envConfig.aemPreviewStats.replaceAll('$eyId', request.aemId),
        {
          headers: {
            Authorization: 'Bearer ' + userDetails.token
          }
        }
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error('Request failed');
          }

          return response.json();
        })
        .then((data) => {
          responseData = [...responseData, data];
        })
        .catch((error) => {
          if (isLocalOrDevEnv) {
            console.log('Error in Preview Stats: ' + error);
          }
        });

      return {
        previewStats: responseData[0]
      };
    }
  );

  // Fetch user preferences to get initial searchMode 

  public fetchUserPreferences = createAsyncThunk<{ searchMode: 'Keyword' | 'Semantic' }, { accounts: Array<Object> }>(
    'results/fetchUserPreferences',
    async (request) => {
      // If toggle AI search feature is disabled, always return Keyword
      if (!envConfig.isToggleAISearchEnabled) {
        return { searchMode: 'Keyword' as const };
      }
      try {
        const userPreferencesConfig = { ...apiConfig.getUserProfileProperties };
        userPreferencesConfig.accountInfo = request.accounts;
        const response = await ApiService.requestApi([userPreferencesConfig]);
        if (response && response[0]) {
          const preferences = response[0] as any;
          const newSearchMode = preferences.enableAISearch ? 'Semantic' as const : 'Keyword' as const;
          if (isLocalOrDevEnv) {
            console.log('Initial searchMode set from API:', newSearchMode, '(enableAISearch:', preferences.enableAISearch, ')');
          }

          return { searchMode: newSearchMode };
        }

        return { searchMode: 'Keyword' as const };
      } catch (error) {
        if (isLocalOrDevEnv) {
          console.warn('Failed to fetch user preferences on load:', error);
        }

        return { searchMode: 'Keyword' as const };
      }
    }
  );

  // Update user's search preference for AI Search and persist it through API call

  public updateToggleAISearch = createAsyncThunk(
    'results/updateToggleAISearch',
    async (request: { accounts: Array<Object>; enableAISearch: boolean }) => {
      try {
        const toggleConfig = { ...apiConfig.toggleAISearch };
        toggleConfig.accountInfo = request.accounts;
        toggleConfig.data = { enableAISearch: request.enableAISearch };
        await ApiService.requestApi([toggleConfig]);
      } catch (error) {
        console.warn('Failed to update AI search preference:', error);
      }
    }
  );
}

const SearchUseCase = new Search();
export default SearchUseCase;
